import EastIcon from "@mui/icons-material/East";
import SearchIcon from "@mui/icons-material/Search";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Typography,
} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import { createContext, useContext, useEffect, useReducer } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useInstantSearch, useSearchBox } from "react-instantsearch";
import { useTheme } from "@mui/material/styles";
import TypesenseInstantSearch, {
  TypesenseIndex,
} from "components/context/TypesenseInstantSearch";
import Button from "components/ui/atoms/Button";
import Highlight from "components/ui/atoms/Highlight";
import SearchBox from "components/ui/atoms/SearchBox";
import CompanyHitShort from "models/companies/CompanyHitShort";
import NoteHitShort from "models/notes/NoteHitShort";
import PeopleHitShort from "models/people/PeopleHitShort";
import useIsMobile from "components/hooks/useIsMobile";
import { useNavigate } from "react-router-dom";
import { toQueryString } from "components/hooks/useQueryStringState";

const Provider = ({ onClick, HitComponent, title, onClickMore, nbResults }) => {
  const { results } = useInstantSearch();

  return (
    <>
      <ListItemButton key="title" onClick={onClickMore}>
        <ListItemText>
          <Typography variant="h6">{title}</Typography>
        </ListItemText>
      </ListItemButton>
      {results.hits.slice(0, nbResults || 3).map((hit) => (
        <HitComponent key={hit.id} onClick={onClick} hit={hit} />
      ))}
      <ListItemButton key="actions" onClick={onClickMore}>
        <ListItemText sx={{ ml: "auto", flexGrow: 0, mr: 1 }}>
          See all
        </ListItemText>
        <EastIcon />
      </ListItemButton>
    </>
  );
};

const SearchContext = createContext();

export const SearchContextProvider = ({ children }) => {
  const reduce = (state, action) => {
    switch (action.type) {
      case "setQuery":
        return {
          query: action.detail,
          open: !!action.detail || state.open,
        };
      case "setOpen":
        return {
          query: action.detail == false ? "" : action.query,
          open: action.detail,
        };
    }
  };

  const [state, dispatch] = useReducer(reduce, { query: "", open: false });

  const setOpen = (open) => {
    dispatch({ type: "setOpen", detail: open });
  };
  const setQuery = (query) => {
    dispatch({ type: "setQuery", detail: query });
  };

  return (
    <SearchContext.Provider
      value={{ open: state.open, query: state.query, setQuery, setOpen }}
    >
      {/* indexName here avoids console error in browser */}
      <TypesenseInstantSearch indexName="company">
        <SearchDialog />
      </TypesenseInstantSearch>
      {children}
    </SearchContext.Provider>
  );
};

const SearchDialog = () => {
  const { open, query, setOpen } = useContext(SearchContext);
  const isMobile = useIsMobile();
  const theme = useTheme();
  const { refine } = useSearchBox();
  const navigate = useNavigate();

  useEffect(() => {
    refine(query);
  }, [query]);

  const onClickCompany = (id) => {
    navigate(`/company/${id}`);
    setOpen(false);
  };

  const onClickPeople = (id, hit) => {
    navigate(`/people/${id}`);
    setOpen(false);
  };

  const onClickNote = (id) => {
    navigate(`/note/${id}`);
    setOpen(false);
  };

  const onClickMoreCompany = () => {
    navigate(`/company${toQueryString({ search: query })}`);
    setOpen(false);
  };
  const onClickMorePeople = () => {
    navigate(`/people${toQueryString({ search: query })}`);
    setOpen(false);
  };
  const onClickMoreNote = () => {
    navigate(`/note${toQueryString({ search: query })}`);
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      fullWidth
      disableRestoreFocus
      fullScreen={isMobile}
    >
      <DialogTitle>
        <Highlight color={theme.palette_custom.yellow}>{"Search"}</Highlight>
      </DialogTitle>
      <DialogContent>
        <List
          subheader={
            <ListSubheader>
              <GlobalSearchBox />
            </ListSubheader>
          }
          dense
        >
          <TypesenseIndex indexName="company">
            <Provider
              onClick={onClickCompany}
              onClickMore={onClickMoreCompany}
              HitComponent={CompanyHitShort}
              title="Company"
            />
          </TypesenseIndex>
          <TypesenseIndex indexName="people">
            <Provider
              onClick={onClickPeople}
              onClickMore={onClickMorePeople}
              HitComponent={PeopleHitShort}
              title="People"
            />
          </TypesenseIndex>
          <TypesenseIndex indexName="note">
            <Provider
              onClick={onClickNote}
              onClickMore={onClickMoreNote}
              HitComponent={NoteHitShort}
              title="Note"
              nbResults={5}
            />
          </TypesenseIndex>
        </List>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={() => setOpen(false)}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SearchDrawerButton = ({ isMobile }) => {
  const { open, setOpen } = useContext(SearchContext);

  useHotkeys(
    "meta+f",
    () => {
      !isMobile && !open ? handleOpen() : handleClose();
    },
    { preventDefault: true },
  );

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <ListItem>
        <Tooltip title="&#8984;+f">
          <Button
            fullWidth
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={() => setOpen(true)}
          >
            Search&nbsp;&nbsp; (<>&#8984;+f</>)
          </Button>
        </Tooltip>
      </ListItem>
    </>
  );
};

export const GlobalSearchBox = (props) => {
  const { query, setQuery } = useContext(SearchContext);

  return (
    <SearchBox
      placeholder="Search for company, people and notes ..."
      handleOnChange={setQuery}
      query={query}
      autoFocus
      {...props}
    />
  );
};

export default SearchDrawerButton;
